/**
 * useListenerManager Hook
 * 
 * React hook for managing global listeners in AppLayout.
 * Handles initialization and cleanup based on auth state.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import listenerManager from './index';
import useNotificationStore from '../../store/useNotificationStore';
import useAlertStore from '../../store/useAlertStore';
import useSessionIdStore from '../../store/useSessionIdStore';
import { getNotifications } from '../../api/notifications';

/**
 * Hook to initialize and manage global listeners
 * 
 * @param {Object} user - Current user object
 * @param {string} orgId - Organization ID
 */
export const useListenerManager = (user, orgId) => {
  const navigate = useNavigate();
  const params = useParams();
  const queryClient = useQueryClient();
  
  const notification = {
    addNotification: useNotificationStore((state) => state.addNotification),
    hydrate: useNotificationStore((state) => state.hydrate),
  };
  
  const alert = {
    setIsOpen: useAlertStore((state) => state.setIsOpen),
  };

  const sessionId = useSessionIdStore((state) => state.session_id);

  useEffect(() => {
    // Only initialize if we have required auth data
    if (!user?.uid || !orgId) {
      return;
    }

    // Prepare context for listener handlers
    const context = {
      user,
      orgId,
      navigate,
      params,
      queryClient,
      notification,
      alert,
      sessionId,
    };

    // Initialize manager
    listenerManager.initialize(context).then(() => {
      listenerManager.subscribeGlobal();
      // Hydrate notification store from persisted backend records
      getNotifications(orgId, user.uid)
        .then((records) => notification.hydrate(records))
        .catch(() => {}); // non-fatal — in-memory store still works
    });

    // Cleanup on unmount or auth change
    return () => {
      listenerManager.unsubscribeGlobal();
      // Don't call cleanup() here - we want manager to persist
      // Only cleanup on actual logout
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, orgId]); // Only re-initialize if auth changes

  // Update context when navigation changes (without re-subscribing)
  useEffect(() => {
    if (user?.uid && orgId) {
      listenerManager.updateContext({
        params,
        navigate,
      });
    }
  }, [params, navigate, user?.uid, orgId]);

  return {
    getActiveListenerCount: () => listenerManager.getActiveListenerCount(),
    cleanup: () => listenerManager.cleanup(), // Expose for logout
  };
};

export default useListenerManager;
